import React from 'react';
import { BreakpointProvider, setDefaultBreakpoints } from 'react-socks';
import { common } from '@kurtosys/ksys-app-template';
import { action, computed, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';
import { ThemeProvider } from 'styled-components';

import { assets as defaultAssets } from '../../../assets/assets.js';
import { DEFAULT_COMPONENT_CONFIGURATION } from '../../../configurations/defaultComponentConfiguration.js';
import { AnalyticsContext, ComponentBase, InjectedStyledComponent } from '../../../shared/index.js';
import { ConfigurationContext } from '../../../shared/withConfiguration.js';
import { AccessibilityContext } from '../Accessibility/Accessibility.js';
import { IAccessibilityContextValue } from '../Accessibility/models/index.js';
import { AssetContext, AssetContextValue } from '../Asset/Asset.js';
import { IAssetContextValue } from '../Asset/models/IAssetContextValue.js';
import { ITranslationContextValue } from '../Translate/models/index.js';
import { TranslationContext } from '../Translate/Translate.js';

import { IApplicationConfiguration, IApplicationProps, IApplicationState } from './models/index.js';
import { FontFaces } from './styledComponents/FontFaces.js';
import { APPLICATION_CATEGORY_KEY, APPLICATION_COMPONENT_KEY } from './Application.meta.js';
export class Application extends ComponentBase<IApplicationProps, IApplicationState, IApplicationConfiguration> {
	categoryKey = APPLICATION_CATEGORY_KEY;
	componentKey = APPLICATION_COMPONENT_KEY;
	static defaultProps: IApplicationProps = {
		theme: {},
		culture: 'en-GB',
		breakpointDefinitions: [
			{ xsmall: 0 }, // all mobile devices
			{ small: 576 }, // mobile devices (not sure which one's this big)
			{ medium: 768 }, // ipad, ipad pro, ipad mini, etc
			{ large: 992 }, // smaller laptops
			{ xlarge: 1200 }, // laptops and desktops
		],
	};

	public appId: string | undefined;

	constructor(props: IApplicationProps) {
		super(props);
		this.loadAppId();
		makeObservable(this, {
			appId: observable,
			configuration: computed,
			loadAppId: action,
		});
		setDefaultBreakpoints(this.props.breakpointDefinitions || []);
	}
	get configuration() {
		const { configuration = {}, appKey } = this.props;
		const mergedConfiguration = common.commonUtils.deepMergeObjects(
			DEFAULT_COMPONENT_CONFIGURATION,
			configuration,
			{
				appKey,
			},
		);
		return mergedConfiguration;
	}

	get translationContextValue(): ITranslationContextValue {
		const { translate = (key: string) => key, culture = 'en-GB' } = this.props;
		return {
			translate,
			culture,
		};
	}
	get assetContextValue(): IAssetContextValue {
		const { assetRegisters, assetCacheOptions } = this.props;
		let { assets } = this.props;
		if (assets && typeof assets === 'object') {
			assets = common.commonUtils.deepMergeObjects(defaultAssets, assets);
		} else {
			assets = defaultAssets;
		}
		return AssetContextValue(assets, assetRegisters, assetCacheOptions);
	}
	get accessibilityContextValue(): IAccessibilityContextValue {
		const { accessibilityText = (key: string) => key } = this.props;
		return { accessibilityText };
	}
	get analyticsContextValue() {
		return this.props.analyticsHelper;
	}

	public loadAppId = (): void => {
		const { appKey } = this.props;
		this.appId = `ksys-app-instance-${appKey || common.commonUtils.generateGuid()}`;
	};

	render() {
		const { theme = {}, children, className } = this.props;
		const baseUrl = this.assetContextValue.baseUrl();
		return (
			<ConfigurationContext.Provider value={this.configuration}>
				<AssetContext.Provider value={this.assetContextValue}>
					<TranslationContext.Provider value={this.translationContextValue}>
						<AccessibilityContext.Provider value={this.accessibilityContextValue}>
							<ThemeProvider theme={theme}>
								<AnalyticsContext.Provider value={this.analyticsContextValue}>
									<FontFaces fontFaces={theme.fontFaces} baseUrl={baseUrl} />
									<BreakpointProvider>
										<section id={this.appId} className={className}>
											{children}
										</section>
									</BreakpointProvider>
								</AnalyticsContext.Provider>
							</ThemeProvider>
						</AccessibilityContext.Provider>
					</TranslationContext.Provider>
				</AssetContext.Provider>
			</ConfigurationContext.Provider>
		);
	}
}

export default InjectedStyledComponent(observer(Application), APPLICATION_CATEGORY_KEY, APPLICATION_COMPONENT_KEY);
